/* ---- <product-tabs> ---- */
(() => {
  'use strict';
  if (customElements.get('product-tabs')) return;

  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const OUT_MS = 160;

  class ProductTabs extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;

      this.list = this.querySelector('[role="tablist"]');
      this.indicator = this.querySelector('.product-tabs__indicator');
      this.box = this.querySelector('.product-tabs__panels');
      const stray = [...this.box.querySelectorAll('[data-tab-button]')];
      stray.forEach((button) => this.list.appendChild(button));
      this.tabs = [...this.list.querySelectorAll('[data-tab-button]')];
      if (stray.length && !this.tabs.some((t) => t.getAttribute('aria-selected') === 'true') && this.tabs[0]) {
        this.tabs[0].setAttribute('aria-selected', 'true');
      }
      if (!this.tabs.length) return;

      this.tabs.forEach((tab) => { if (!this.panel(tab)) tab.hidden = true; });
      this.tabs = this.tabs.filter((tab) => !tab.hidden);
      this.index = Math.max(0, this.tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true'));
      this.tabs.forEach((tab, i) => {
        const on = i === this.index;
        tab.setAttribute('aria-selected', String(on));
        tab.tabIndex = on ? 0 : -1;
        const panel = this.panel(tab);
        if (panel) panel.hidden = !on;
        tab.addEventListener('click', () => this.select(i));
      });
      this.list.addEventListener('keydown', (e) => this.onKey(e));
      this.bindMouseScroll();

      this.classList.add('is-instant');
      this.classList.add('is-ready');
      requestAnimationFrame(() => requestAnimationFrame(() => this.classList.remove('is-instant')));

      this.resizer = new ResizeObserver(() => {
        this.classList.add('is-instant');
        this.moveIndicator();
        requestAnimationFrame(() => this.classList.remove('is-instant'));
      });
      this.resizer.observe(this.list);
      document.fonts?.ready?.then(() => this.moveIndicator());

      if (window.Shopify?.designMode) {
        this.onBlockSelect = (e) => {
          const panel = e.target?.closest?.('[data-tab-panel]');
          if (!panel || !this.contains(panel)) return;
          const i = this.tabs.findIndex((t) => t.getAttribute('aria-controls') === panel.id);
          if (i >= 0) this.select(i, { instant: true });
        };
        document.addEventListener('shopify:block:select', this.onBlockSelect);
      }
    }

    disconnectedCallback() {
      this.resizer?.disconnect();
      if (this.onBlockSelect) document.removeEventListener('shopify:block:select', this.onBlockSelect);
      this.cancel();
      this.bound = false;
    }

    // The tab bar scrolls sideways with touch, but its scrollbar is hidden, so a mouse (desktop at narrow widths, the
    // theme editor's mobile preview) had no way to reach the far tabs. Drag it, or turn the wheel over it, to scroll.
    bindMouseScroll() {
      const list = this.list;
      const overflows = () => list.scrollWidth - list.clientWidth > 1;
      let id = null, startX = 0, startLeft = 0, dragged = false;
      list.addEventListener('pointerdown', (e) => {
        dragged = false;
        if (e.pointerType !== 'mouse' || e.button !== 0 || !overflows()) return;
        id = e.pointerId; startX = e.clientX; startLeft = list.scrollLeft;
      });
      list.addEventListener('pointermove', (e) => {
        if (e.pointerId !== id) return;
        const dx = e.clientX - startX;
        if (!dragged) {
          if (Math.abs(dx) < 5) return;
          dragged = true;
          try { list.setPointerCapture(id); } catch (err) { /* pointer already released */ }
          list.classList.add('is-dragging');
        }
        list.scrollLeft = startLeft - dx;
      });
      const end = (e) => {
        if (e.pointerId !== id) return;
        id = null;
        list.classList.remove('is-dragging');
      };
      list.addEventListener('pointerup', end);
      list.addEventListener('pointercancel', end);
      // A drag ends with a click on whichever tab is under the pointer; swallow it so dragging never switches tabs.
      list.addEventListener('click', (e) => { if (dragged) { e.stopPropagation(); e.preventDefault(); dragged = false; } }, true);
      list.addEventListener('wheel', (e) => {
        if (!overflows() || Math.abs(e.deltaX) >= Math.abs(e.deltaY)) return;
        const max = list.scrollWidth - list.clientWidth;
        const next = Math.max(0, Math.min(max, list.scrollLeft + e.deltaY));
        if (next === list.scrollLeft) return;
        e.preventDefault();
        list.scrollLeft = next;
      }, { passive: false });
    }

    panel(tab) { return tab && document.getElementById(tab.getAttribute('aria-controls')); }

    moveIndicator() {
      const tab = this.tabs[this.index];
      if (!tab || !this.indicator) return;
      const width = tab.offsetWidth, left = tab.offsetLeft;
      this.indicator.style.width = `${width}px`;
      this.indicator.style.transform = `translateX(${left}px)`;
    }

    reveal(tab) {
      const { scrollLeft, clientWidth } = this.list;
      const left = tab.offsetLeft - 24;
      const right = tab.offsetLeft + tab.offsetWidth + 24;
      if (left < scrollLeft) this.list.scrollTo({ left, behavior: REDUCED ? 'auto' : 'smooth' });
      else if (right > scrollLeft + clientWidth) this.list.scrollTo({ left: right - clientWidth, behavior: REDUCED ? 'auto' : 'smooth' });
    }

    onKey(e) {
      const last = this.tabs.length - 1;
      let i = null;
      if (e.key === 'ArrowRight') i = this.index >= last ? 0 : this.index + 1;
      else if (e.key === 'ArrowLeft') i = this.index <= 0 ? last : this.index - 1;
      else if (e.key === 'Home') i = 0;
      else if (e.key === 'End') i = last;
      if (i === null) return;
      e.preventDefault();
      this.select(i, { focus: true });
    }

    select(i, { focus = false, instant = false } = {}) {
      if (i === this.index || !this.tabs[i]) return;
      const toTab = this.tabs[i];
      const to = this.panel(toTab);
      this.index = i;

      this.tabs.forEach((tab, n) => {
        tab.setAttribute('aria-selected', String(n === i));
        tab.tabIndex = n === i ? 0 : -1;
      });
      if (focus) toTab.focus({ preventScroll: true });
      this.moveIndicator();
      this.reveal(toTab);

      this.cancel();
      const panels = this.tabs.map((tab) => this.panel(tab)).filter(Boolean);
      panels.forEach((p) => p.classList.remove('is-leaving', 'is-entering'));
      const showing = panels.filter((p) => !p.hidden && p !== to);

      if (REDUCED || instant || !to || !showing.length) {
        panels.forEach((p) => { p.hidden = p !== to; });
        this.box.style.height = '';
        this.settled();
        return;
      }

      const [from, ...rest] = showing;
      rest.forEach((p) => { p.hidden = true; });
      to.hidden = true;
      this.box.style.height = `${this.box.offsetHeight}px`;
      from.classList.add('is-leaving');

      this.timer = setTimeout(() => {
        from.hidden = true;
        from.classList.remove('is-leaving');
        to.querySelectorAll('[data-tab-item]').forEach((item, n) => item.style.setProperty('--i', n));
        to.hidden = false;
        to.classList.add('is-entering');
        const target = to.offsetHeight + (this.box.offsetHeight - this.box.clientHeight);
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.box.style.height = `${target}px`;
          this.onBoxEnd = (e) => {
            if (e.target !== this.box || e.propertyName !== 'height') return;
            this.box.removeEventListener('transitionend', this.onBoxEnd);
            this.onBoxEnd = null;
            this.box.style.height = '';
            this.settled();
          };
          this.box.addEventListener('transitionend', this.onBoxEnd);
          this.timer = setTimeout(() => {
            this.cancel();
            this.box.style.height = '';
            to.classList.remove('is-entering');
            this.settled();
          }, 900);
        });
      }, OUT_MS);
    }

    cancel() {
      clearTimeout(this.timer);
      this.timer = null;
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = null;
      if (this.onBoxEnd) this.box.removeEventListener('transitionend', this.onBoxEnd);
      this.onBoxEnd = null;
    }

    settled() {
      window.lenis?.resize?.();
    }
  }

  customElements.define('product-tabs', ProductTabs);
})();
